// Lightweight client-side analytics tracker
import axios from "axios";
import { isAnalyticsAllowed } from "./consent";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;
const SESSION_KEY = "sws_session_id";
const SCROLL_KEY = "sws_scroll_fired";
const IS_DEV = process.env.NODE_ENV !== "production";

let sessionId = null;
let started = false;
let lastPath = null;

// Reset started state when consent changes, so re-accept re-inits
if (typeof window !== "undefined") {
  window.addEventListener("sws:consent-changed", () => {
    started = false;
    sessionId = null;
    lastPath = null;
  });
}

function logDev(context, error) {
  if (IS_DEV) {
    console.warn(`[analytics] ${context}`, error);
  }
}

function getStored() {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch (error) {
    logDev("localStorage read failed", error);
    return null;
  }
}

function setStored(id) {
  try {
    localStorage.setItem(SESSION_KEY, id);
  } catch (error) {
    logDev("localStorage write failed", error);
  }
}

async function safePost(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, { timeout: 6000 });
    return res.data;
  } catch (error) {
    logDev(`POST ${path} failed`, error);
    return null;
  }
}

export async function initSession() {
  if (started) return sessionId;
  if (!isAnalyticsAllowed()) return null;
  started = true;

  const existing = getStored();
  const payload = {
    session_id: existing || undefined,
    referrer: document.referrer || "",
    entry_page: window.location.pathname,
    user_agent: navigator.userAgent,
    language: navigator.language,
    screen: `${window.screen.width}x${window.screen.height}`,
  };
  const data = await safePost("/track/session", payload);
  if (data?.session_id) {
    sessionId = data.session_id;
    setStored(sessionId);
  } else if (existing) {
    sessionId = existing;
  }
  return sessionId;
}

export async function trackPageview(path) {
  if (!isAnalyticsAllowed()) return;
  if (!sessionId) await initSession();
  if (!sessionId || lastPath === path) return;
  lastPath = path;
  try {
    sessionStorage.removeItem(SCROLL_KEY);
  } catch (error) {
    logDev("sessionStorage clear failed", error);
  }
  await safePost("/track/pageview", {
    session_id: sessionId,
    path,
    referrer: document.referrer || "",
  });
}

export async function trackEvent(event_type, meta = {}) {
  if (!isAnalyticsAllowed()) return;
  if (!sessionId) await initSession();
  if (!sessionId) return;
  await safePost("/track/event", { session_id: sessionId, event_type, meta });
}

export function getSessionId() {
  return sessionId || getStored();
}

// Fires a scroll_75 event the first time the user reaches 75% depth on a page
export function attachScrollTracker() {
  const onScroll = () => {
    const scrolled = window.scrollY + window.innerHeight;
    const total = document.documentElement.scrollHeight;
    if (total <= 0) return;
    const pct = scrolled / total;
    if (pct >= 0.75) {
      let fired = false;
      try {
        fired = sessionStorage.getItem(SCROLL_KEY) === "1";
      } catch (error) {
        logDev("sessionStorage read failed", error);
      }
      if (!fired) {
        try {
          sessionStorage.setItem(SCROLL_KEY, "1");
        } catch (error) {
          logDev("sessionStorage write failed", error);
        }
        trackEvent("scroll_75", { path: window.location.pathname });
      }
    }
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
}
